import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IFButton,
  IFDialog,
  IFGroupingComponent,
  IFSearchableCheckPicker,
  IFSelect,
  IFSkeleton,
  IFText,
  IFTextInput,
  IFToggle,
} from 'Components'
import PropTypes from 'prop-types'
import { ChargePointsContainer } from 'Containers'
import Styles from './OperatorAlertModal.module.css'
import { Colors } from 'Theme'
import { connect } from 'react-redux'
import OperatorAlertsSelectors from 'Stores/Alert/Selectors'
import moment from 'moment'
import InfoIcon from '@mui/icons-material/Info'
import { Popover } from '@mui/material'
import { Box } from '@mui/system'
import InfinityEnums from 'Enums/InfinityEnums'
import { DatePicker, TagPicker } from 'rsuite'
import OperatorUserSelectors from 'Stores/OperatorUser/Selectors'
import OperatorUserActions from 'Stores/OperatorUser/Actions'
import IFFilterType from 'Enums/IFFilterType'
import RequestState from 'Enums/RequestState'
import OperatorAlertsActions from 'Stores/Alert/Actions'
import AuthSelectors from 'Stores/Auth/Selectors'
import { sameDate } from 'Utils/dateTimeOperations'
import { formatStatuses } from 'Utils/Format'
import { sameArrayUnordered } from 'Utils/arrayOperations'
import validator from 'validator'
import styled from 'styled-components'

const StyledTagPicker = styled(TagPicker)`
  .rs-picker-tag-wrapper .rs-tag {
    background-color: ${Colors.grey} !important;
  }
`
const OperatorAlertDetails = ({
  clickedOperatorAlert,
  operatorUsersFilters,
  operatorUsersPagination,
  fetchOperatorUserList,
  setOperatorUserFilter,
  clearOperatorUsers,
  fetchOperatorUserListRequestState,
  operatorUsersAlertEmails,
  editAlert,
  editAlertRequestState,
  operatorUser,
  deleteAlert,
  deleteAlertRequestState,
  isNew = false,
  createAlert,
  createAlertRequestState,
}) => {
  const { t } = useTranslation()
  const canEditAlert = operatorUser.permissions.includes(
    InfinityEnums.OperatorUserPermission.CAN_EDIT_ALERT,
  )
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const isPastDay = (d) => startOfDay(d) < startOfDay(new Date())
  const deleteModalRef = useRef(null)
  const chargePointSpecsEditRef = useRef()
  const [vars, setVars] = useState({
    channel: clickedOperatorAlert?.alertType,
    statuses: formatStatuses(clickedOperatorAlert?.trackedStatus),
    duration: Number(clickedOperatorAlert?.duration),
    repeat: Number(clickedOperatorAlert?.sendAlertEvery),
  })
  useEffect(() => {
    if (isNew || !clickedOperatorAlert) {
      setVars({ channel: '—', statuses: '—', duration: '—', repeat: '—' })
      return
    }
    setVars({
      channel: clickedOperatorAlert?.alertType || '—',
      statuses: formatStatuses(clickedOperatorAlert?.trackedStatus),
      duration: Number(clickedOperatorAlert?.duration),
      repeat: Number(clickedOperatorAlert?.sendAlertEvery),
    })
  }, [isNew, clickedOperatorAlert])

  const typeOptions = useMemo(
    () =>
      Object.entries(InfinityEnums.AlertType).map(([key, val]) => ({
        label: val,
        value: val,
      })),
    [],
  )
  const statusOptions = useMemo(
    () =>
      Object.entries(InfinityEnums.OperatorAlertStatus).map(([key, val]) => ({
        label: val,
        value: val,
      })),
    [],
  )
  const options = operatorUsersAlertEmails.map((u) => ({
    label: u.email,
    value: u.email,
    name: u.name,
    email: u.email,
  }))

  const [isEditing, setIsEditing] = useState(isNew ? true : false)
  const [name, setName] = useState(isNew ? '' : clickedOperatorAlert?.name)
  const [duration, setDuration] = useState(isNew ? '' : Number(vars.duration))
  const [sendAlertEvery, setSendAlertEvery] = useState(
    isNew ? '' : Number(vars.repeat),
  )
  const [alertType, setAlertType] = useState(
    isNew ? '' : clickedOperatorAlert?.alertType,
  )
  const [expiryDate, setExpiryDate] = useState(
    isNew
      ? null
      : clickedOperatorAlert?.disableAlertAfter
      ? new Date(clickedOperatorAlert.disableAlertAfter)
      : null,
  )
  const [statusValues, setStatusValues] = useState(
    isNew ? [] : clickedOperatorAlert?.trackedStatus || [],
  )
  const [selectedEmails, setSelectedEmails] = useState(
    isNew ? [] : clickedOperatorAlert?.emails || [],
  )
  const [isActive, setIsActive] = useState(
    isNew ? false : clickedOperatorAlert?.isActive,
  )
  const [isGlobal, setIsGlobal] = useState(
    isNew ? false : clickedOperatorAlert?.isGlobal,
  )
  const [percentageAffected, setPercentageAffected] = useState(
    isNew ? '' : clickedOperatorAlert?.percentageAffected || 0,
  )
  const [interval, setInterval] = useState(
    isNew ? '' : clickedOperatorAlert?.interval || 0,
  )
  const [nameError, setNameError] = useState()
  const [statusError, setStatusError] = useState()
  const [selectedEmailsError, setSelectedEmailsError] = useState()
  const [durationError, setDurationError] = useState()
  const [typeError, setTypeError] = useState()
  const [intervalError, setIntervalError] = useState()
  const [repeatError, setRepeatError] = useState()
  const [percentageAffectedError, setPercentageAffectedError] = useState()

  const [addedChargePoints, setAddedChargePoints] = useState([])
  const displayedList = useMemo(() => {
    const originalIsGlobal = !!clickedOperatorAlert?.isGlobal

    if (isNew) {
      return addedChargePoints
    }

    if (isEditing && isGlobal !== originalIsGlobal) {
      return []
    }

    const list = clickedOperatorAlert?.chargePointsFormatted ?? []

    return list.map((cp) => ({
      stationName: cp.stationName ?? '',
      chargePointHuid: cp.chargePointHuid ?? '',
      _id: cp._id ?? null,
    }))
  }, [clickedOperatorAlert, isEditing, isGlobal, isNew, addedChargePoints])
  useEffect(() => {
    if (isEditing && isGlobal !== clickedOperatorAlert?.isGlobal) {
      setAddedChargePoints([])
    }
  }, [isGlobal, isEditing, clickedOperatorAlert])

  const handleSearch = (searchQuery) => {
    clearOperatorUsers()
    const updatedFilters = operatorUsersFilters.map((flt) =>
      flt.type === IFFilterType.KEYWORD
        ? {
            ...flt,
            value: searchQuery ? [searchQuery] : [],
          }
        : flt,
    )
    setOperatorUserFilter(updatedFilters)
  }
  const renderMenuItem = (label, item) => (
    <div className={Styles.MenuItemRow}>
      {label === 'Skeleton' ? (
        <div className={Styles.Skeleton}>
          <IFSkeleton variant="text" />
        </div>
      ) : (
        <div className={Styles.EmailItem}>
          <IFText
            className={Styles.MenuItemName}
            style={{ color: Colors.text }}
          >
            {item?.name || '(No name)'}
          </IFText>
          <IFText
            className={Styles.MenuItemEmail}
            style={{ color: Colors.AlertItem.SettingsTitle }}
          >
            {item?.email}
          </IFText>
        </div>
      )}
    </div>
  )

  const loadMoreOperatorUsers = () => {
    if (
      fetchOperatorUserListRequestState === RequestState.LOADING_MORE ||
      operatorUsersPagination === null
    )
      return
    fetchOperatorUserList(operatorUsersFilters, operatorUsersPagination)
  }

  const [anchorEl, setAnchorEl] = useState(null)
  const [openKey, setOpenKey] = useState(null)
  const open = Boolean(anchorEl) && openKey !== null

  const contentMap = useMemo(
    () => ({
      type: t('OperatorAlertList.TypePopover'),
      status: t('OperatorAlertList.StatusPopover'),
      duration: t('OperatorAlertList.DurationPopover'),
      repeatAfter: t('OperatorAlertList.RepeatAfterPopover'),
      global: t('OperatorAlertList.GlobalPopover'),
      percentageAffected: t('OperatorAlertList.PercentageAffectedPopover'),
      interval: t('OperatorAlertList.IntervalPopover'),
    }),
    [t],
  )
  const validateCreate = () => {
    let valid = true
    const nameVal = String(name ?? '').trim()
    if (!nameVal) {
      setNameError(t('OperatorAlertList.Required'))
      valid = false
    } else if (!validator.isLength(nameVal, { min: 3 })) {
      setNameError(t('OperatorAlertList.NameMustBeAtLeastN', { n: 3 }))
      valid = false
    } else {
      setNameError(undefined)
    }
    if (!alertType) {
      setTypeError(t('OperatorAlertList.Required'))
      valid = false
    } else {
      setTypeError(undefined)
    }
    if (!Array.isArray(statusValues) || statusValues.length === 0) {
      setStatusError(t('OperatorAlertList.Required'))
      valid = false
    } else {
      setStatusError(undefined)
    }
    if (!Array.isArray(selectedEmails) || selectedEmails.length === 0) {
      setSelectedEmailsError(t('OperatorAlertList.Required'))
      valid = false
    } else {
      setSelectedEmailsError(undefined)
    }

    const durStr = String(duration ?? '').trim()
    if (!durStr) {
      setDurationError(t('OperatorAlertList.Required'))
      valid = false
    } else if (!validator.isInt(durStr, { min: 0.1 })) {
      setDurationError(t('OperatorAlertList.MustBeNumber'))
      valid = false
    } else {
      setDurationError(undefined)
    }
    const intStr = String(interval ?? '').trim()
    if (!intStr) {
      setIntervalError(t('OperatorAlertList.Required'))
      valid = false
    } else if (!validator.isInt(intStr, { min: 0.1 })) {
      setIntervalError(t('OperatorAlertList.MustBeNumber'))
      valid = false
    } else {
      setIntervalError(undefined)
    }

    return valid
  }

  const handlePopoverOpen = (e, key) => {
    setAnchorEl(e.currentTarget)
    setOpenKey(key)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
    setOpenKey(null)
  }

  const onSaveClick = () => {
    const cpPayload = chargePointSpecsEditRef.current?.handleSubmit(false) || {}
    const finalHuids = (cpPayload.finalList ?? [])
      .map((cp) => cp.chargePointHuid)
      .filter(Boolean)

    const payload = {
      name: name.trim(),
      alertType,
      trackedStatus: statusValues,
      duration: Number(duration),
      interval: Number(interval),
      sendAlertEvery: Number(sendAlertEvery),
      disableAlertAfter: expiryDate ? expiryDate.toISOString() : null,
      emails: selectedEmails,
      isActive,
      isGlobal,
      percentageAffected: Number(percentageAffected) || 0,
      ...(isGlobal
        ? { exclude: finalHuids }
        : {
            chargePoints: finalHuids.reduce(
              (acc, h) => ((acc[h] = null), acc),
              {},
            ),
          }),
    }
    if (isNew) {
      if (!validateCreate()) return

      createAlert(payload)
    } else {
      const changes = {}

      if ((name ?? '') !== clickedOperatorAlert.name)
        changes.name = (name ?? '').trim()

      if (Number(duration) !== clickedOperatorAlert.duration)
        changes.duration = Number(duration)

      if (Number(sendAlertEvery) !== clickedOperatorAlert.sendAlertEvery)
        changes.sendAlertEvery = Number(sendAlertEvery)

      if ((alertType ?? '') !== clickedOperatorAlert.alertType)
        changes.alertType = alertType ?? ''

      if (!sameDate(expiryDate, clickedOperatorAlert.disableAlertAfter))
        changes.disableAlertAfter = expiryDate ? expiryDate.toISOString() : null

      if (!sameArrayUnordered(statusValues, clickedOperatorAlert.trackedStatus))
        changes.trackedStatus = statusValues

      if (!sameArrayUnordered(selectedEmails, clickedOperatorAlert.emails))
        changes.emails = selectedEmails

      if (isActive !== clickedOperatorAlert.isActive)
        changes.isActive = isActive

      if (isGlobal !== clickedOperatorAlert.isGlobal)
        changes.isGlobal = isGlobal

      if (percentageAffected !== clickedOperatorAlert.percentageAffected) {
        changes.percentageAffected = percentageAffected
      }
      if (interval !== clickedOperatorAlert.interval) {
        changes.interval = interval
      }

      if (
        (cpPayload.addIds && cpPayload.addIds.length) ||
        (cpPayload.deleteIds && cpPayload.deleteIds.length)
      ) {
        const finalHuids = Array.from(
          new Set(
            (cpPayload.finalList ?? [])
              .map((cp) => cp.chargePointHuid)
              .filter(Boolean),
          ),
        )
        if (isGlobal) {
          changes.exclude = finalHuids
        } else {
          changes.chargePoints = finalHuids.reduce((acc, huid) => {
            acc[huid] = null
            return acc
          }, {})
        }
      }
      if (Object.keys(changes).length > 0) {
        editAlert(clickedOperatorAlert._id, changes)
      } else {
        setIsEditing(false)
      }
    }
  }
  const onCancelClick = () => {
    chargePointSpecsEditRef.current?.handleCancel?.()
    setName(clickedOperatorAlert.name)
    setDuration(vars.duration)
    setSendAlertEvery(vars.repeat)
    setAlertType(clickedOperatorAlert.alertType)
    setExpiryDate(
      clickedOperatorAlert.disableAlertAfter
        ? new Date(clickedOperatorAlert.disableAlertAfter)
        : null,
    )
    setStatusValues(
      clickedOperatorAlert?.trackedStatus
        ? clickedOperatorAlert.trackedStatus
        : [],
    )
    setSelectedEmails(
      clickedOperatorAlert?.emails ? clickedOperatorAlert.emails : [],
    )
    setIsActive(clickedOperatorAlert.isActive)
    setIsGlobal(clickedOperatorAlert.isGlobal)
    setPercentageAffected(clickedOperatorAlert.percentageAffected || 0)
    setInterval(clickedOperatorAlert.interval || 0)

    setNameError()
    setTypeError()
    setStatusError()
    setSelectedEmailsError()
    setDurationError()
    setIntervalError()
    setPercentageAffectedError()
    setRepeatError()

    setIsEditing(false)
  }

  const onDeleteClickHandler = () => {
    deleteModalRef.current.show()
  }
  const handleDelete = () => {
    deleteAlert(clickedOperatorAlert._id)
  }
  useEffect(() => {
    fetchOperatorUserList(operatorUsersFilters, operatorUsersPagination)
  }, [operatorUsersFilters])
  useEffect(() => {
    if (editAlertRequestState === RequestState.SUCCEEDED) {
      setIsEditing(false)
    }
  }, [editAlertRequestState])

  return (
    <div className={Styles.container}>
      <div className={Styles.BodyScroll}>
        <div className={Styles.AlertDescription}>
          <IFText className={Styles.AlertDescriptionText}>
            {t('OperatorAlertList.FirstPart')}
            <strong>{vars.channel}</strong> {t('OperatorAlertList.SecondPart')}
            <strong>{vars.statuses}</strong> {t('OperatorAlertList.ThirdPart')}
            <strong>{vars.duration}</strong> {t('OperatorAlertList.FourthPart')}{' '}
            <strong>{vars.repeat}</strong> {t('OperatorAlertList.Hours')}
          </IFText>
        </div>

        <div
          className={Styles.VerticalDivider}
          style={{ backgroundColor: Colors.grey }}
        />

        <div className={Styles.SettingsContainer}>
          <div className={Styles.SettingsPart}>
            <IFText className={Styles.SettingsTitle}>
              {t('OperatorAlertList.AlertSettings')}
            </IFText>

            <div className={Styles.SettingsContent}>
              <div className={Styles.SettingItem}>
                <IFText
                  className={Styles.SettingTitle}
                  style={{ color: Colors.AlertItem.SettingsTitle }}
                >
                  {t('OperatorAlertList.Name')}
                </IFText>

                {isEditing ? (
                  <IFTextInput
                    className={Styles.SettingValueInput}
                    value={name}
                    onChange={(e) => {
                      const value = e.target.value
                      setName(value)

                      if (validator.isEmpty(value.trim())) {
                        setNameError(t('OperatorAlertList.Required'))
                      } else if (
                        !validator.isLength(value.trim(), { min: 3 })
                      ) {
                        setNameError(
                          t('OperatorAlertList.NameMustBeAtLeastN', { n: 3 }),
                        )
                      } else {
                        setNameError(undefined)
                      }
                    }}
                    placeholder={t('OperatorAlertList.Name')}
                    onBlur={() => {
                      const value = name ?? ''
                      if (validator.isEmpty(value.trim())) {
                        setNameError(t('OperatorAlertList.Required'))
                      } else if (
                        !validator.isLength(value.trim(), { min: 3 })
                      ) {
                        setNameError(
                          t('OperatorAlertList.NameMustBeAtLeastN', { n: 3 }),
                        )
                      } else {
                        setNameError()
                      }
                    }}
                    errorText={nameError}
                  />
                ) : (
                  <div className={Styles.ParentFlexContainer}>
                    <IFText className={Styles.SettingValue}>
                      {clickedOperatorAlert?.name || '—'}
                    </IFText>
                  </div>
                )}
              </div>

              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.Type')}
                  </IFText>

                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) => handlePopoverOpen(e, 'type')}
                    onMouseLeave={handlePopoverClose}
                  />
                </div>

                {isEditing ? (
                  <div className={Styles.FieldAbs}>
                    <IFSelect
                      menuMaxHeight={100}
                      data={typeOptions}
                      value={alertType}
                      onChange={(selectedValue) => {
                        setAlertType(selectedValue)

                        if (!selectedValue || selectedValue.length === 0) {
                          setTypeError(t('OperatorAlertList.Required'))
                        } else {
                          setTypeError('')
                        }
                      }}
                      style={{
                        minWidth: '12rem',
                        maxWidth: '12rem',
                        minHeight: '1.5rem',
                      }}
                      size="sm"
                      canLoadMore={false}
                      searchable={false}
                      handleClosePicker={() => {
                        if (!alertType || alertType.length === 0) {
                          setTypeError(t('OperatorAlertList.Required'))
                        } else {
                          setTypeError('')
                        }
                      }}
                    />
                    {typeError && (
                      <div className={Styles.ErrorText}>{typeError}</div>
                    )}
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {clickedOperatorAlert?.alertType || ''}
                  </IFText>
                )}
              </div>

              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.Status')}
                  </IFText>
                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) => handlePopoverOpen(e, 'status')}
                    onMouseLeave={handlePopoverClose}
                  />
                </div>
                {isEditing ? (
                  <div className={Styles.FieldAbs}>
                    <StyledTagPicker
                      data={statusOptions}
                      value={statusValues}
                      onChange={(vals) => {
                        setStatusValues(vals || [])

                        if (!vals || vals.length === 0) {
                          setStatusError(t('OperatorAlertList.Required'))
                        } else {
                          setStatusError('')
                        }
                      }}
                      size="sm"
                      block
                      cleanable
                      searchable={false}
                      style={{
                        minWidth: '12rem',
                        maxWidth: '14rem',
                        minHeight: '1.5rem',
                      }}
                      onClose={() => {
                        if (!statusValues || statusValues.length === 0) {
                          setStatusError(t('OperatorAlertList.Required'))
                        } else {
                          setStatusError('')
                        }
                      }}
                      errorText={statusError}
                      aria-invalid={!!statusError}
                    />
                    {statusError && (
                      <div className={Styles.ErrorText}>{statusError}</div>
                    )}
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {vars.statuses}
                  </IFText>
                )}
              </div>

              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.Duration')}
                  </IFText>
                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) => handlePopoverOpen(e, 'duration')}
                    onMouseLeave={handlePopoverClose}
                  />
                </div>
                {isEditing ? (
                  <div className={Styles.SettingValueRow}>
                    <IFText>{t('OperatorAlertList.MoreThan')} </IFText>

                    <IFTextInput
                      className={Styles.SettingValueInputNumber}
                      value={duration}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setDuration(raw)
                        if (value === '') {
                          setDurationError(t('OperatorAlertList.Required'))
                          return
                        }
                        if (!validator.isInt(value, { min: 0.1 })) {
                          setDurationError(t('OperatorAlertList.MustBeNumber'))
                          return
                        }
                        setDurationError(undefined)
                      }}
                      onBlur={() => {
                        const value = String(duration ?? '').trim()
                        if (value === '') {
                          setDurationError(t('OperatorAlertList.Required'))
                        } else if (!validator.isInt(value, { min: 0.1 })) {
                          setDurationError(t('OperatorAlertList.MustBeNumber'))
                        } else {
                          setDurationError(undefined)
                        }
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      errorText={durationError}
                      placeholder={t('OperatorAlertList.NumberExample')}
                    />

                    <IFText className={Styles.SettingValue}>
                      {t('OperatorAlertList.Minutes')}
                    </IFText>
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {t('OperatorAlertList.MoreThan')} {vars.duration}
                    {t('OperatorAlertList.Minutes')}
                  </IFText>
                )}
              </div>

              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.RepeatAfter')}
                  </IFText>
                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) => handlePopoverOpen(e, 'repeatAfter')}
                    onMouseLeave={handlePopoverClose}
                  />
                </div>
                {isEditing ? (
                  <div className={Styles.SettingValueRow}>
                    <IFTextInput
                      className={Styles.SettingValueInputNumber}
                      value={sendAlertEvery}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setSendAlertEvery(Number(raw))
                        if (value === '') {
                          setRepeatError(undefined)
                          return
                        }

                        if (!validator.isInt(value, { min: 0 })) {
                          setRepeatError(t('OperatorAlertList.MustBeNumber'))
                          return
                        }
                        setRepeatError(undefined)
                      }}
                      errorText={repeatError}
                      placeholder={t('OperatorAlertList.NumberExample')}
                    />

                    <IFText>{t('OperatorAlertList.Hours')}</IFText>
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {vars.repeat} {t('OperatorAlertList.Hours')}
                  </IFText>
                )}
              </div>
              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.PercentageAffected')}
                  </IFText>
                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) =>
                      handlePopoverOpen(e, 'percentageAffected')
                    }
                    onMouseLeave={handlePopoverClose}
                  />
                </div>
                {isEditing ? (
                  <div className={Styles.SettingValueRow}>
                    <IFTextInput
                      className={Styles.SettingValueInputNumber}
                      value={percentageAffected}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setPercentageAffected(Number(raw))
                        if (value === '') {
                          setPercentageAffectedError(undefined)
                          return
                        }

                        if (!validator.isFloat(value, { min: 0 })) {
                          setPercentageAffectedError(
                            t('OperatorAlertList.MustBeNumber'),
                          )
                          return
                        }
                        setPercentageAffectedError(undefined)
                      }}
                      errorText={percentageAffectedError}
                      placeholder={t('OperatorAlertList.NumberExample')}
                    />

                    <IFText>{t('OperatorAlertList.percentage')}</IFText>
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {percentageAffected} {t('OperatorAlertList.percentage')}
                  </IFText>
                )}
              </div>
              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.Interval')}
                  </IFText>
                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) => handlePopoverOpen(e, 'interval')}
                    onMouseLeave={handlePopoverClose}
                  />
                </div>
                {isEditing ? (
                  <div className={Styles.SettingValueRow}>
                    <IFTextInput
                      className={Styles.SettingValueInputNumber}
                      value={interval}
                      onChange={(e) => {
                        const raw = e.target.value
                        const value = raw.trim()
                        setInterval(raw)
                        if (value === '') {
                          setIntervalError(t('OperatorAlertList.Required'))
                          return
                        }
                        if (!validator.isInt(value, { min: 0.1 })) {
                          setIntervalError(t('OperatorAlertList.MustBeNumber'))
                          return
                        }
                        setIntervalError(undefined)
                      }}
                      onBlur={() => {
                        const value = String(interval ?? '').trim()
                        if (value === '') {
                          setIntervalError(t('OperatorAlertList.Required'))
                        } else if (!validator.isInt(value, { min: 0.1 })) {
                          setIntervalError(t('OperatorAlertList.MustBeNumber'))
                        } else {
                          setIntervalError(undefined)
                        }
                      }}
                      errorText={intervalError}
                      placeholder={t('OperatorAlertList.NumberExample')}
                    />
                    {t('OperatorAlertList.Minutes')}
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {interval} {t('OperatorAlertList.Minutes')}
                  </IFText>
                )}
              </div>

              <div className={Styles.SettingItem}>
                <IFText
                  className={Styles.SettingTitle}
                  style={{ color: Colors.AlertItem.SettingsTitle }}
                >
                  {t('OperatorAlertList.DisableAfter')}
                </IFText>

                {isEditing ? (
                  <div className={Styles.SettingValueRow}>
                    <DatePicker
                      className={Styles.DatePicker}
                      value={expiryDate}
                      onChange={(val) => setExpiryDate(val)}
                      cleanable
                      size="sm"
                      placeholder="YYYY-MM-DD"
                      format="yyyy-MM-dd"
                      disabledDate={isPastDay}
                      style={{
                        minWidth: '12rem',
                        maxWidth: '12rem',
                        minHeight: '1.5rem',
                      }}
                      placement="autoVerticalStart"
                    />
                  </div>
                ) : (
                  <IFText className={Styles.SettingValue}>
                    {clickedOperatorAlert?.disableAlertAfter
                      ? moment(clickedOperatorAlert.disableAlertAfter).format(
                          'MMM DD, YYYY LTS',
                        )
                      : '—'}
                  </IFText>
                )}
              </div>

              <div className={Styles.SettingItem}>
                <IFText
                  className={Styles.SettingTitle}
                  style={{ color: Colors.AlertItem.SettingsTitle }}
                >
                  {t('OperatorAlertList.OperatorUsers')}
                </IFText>
                {isEditing ? (
                  <div className={Styles.FieldAbs}>
                    <IFSearchableCheckPicker
                      data={options}
                      loading={
                        fetchOperatorUserListRequestState ===
                        RequestState.LOADING
                      }
                      paginationOffset={operatorUsersPagination}
                      onSearch={handleSearch}
                      loadMore={loadMoreOperatorUsers}
                      value={selectedEmails}
                      onChange={(vals) => {
                        setSelectedEmails(vals || [])
                        if (!vals || vals.length === 0) {
                          setSelectedEmailsError(
                            t('OperatorAlertList.Required'),
                          )
                        } else {
                          setSelectedEmailsError('')
                        }
                      }}
                      size="sm"
                      style={{
                        minWidth: '12rem',
                        maxWidth: '12rem',
                        minHeight: '1.5rem',
                      }}
                      cleanable
                      renderMenuItem={renderMenuItem}
                      handleClosePicker={() => {
                        setOperatorUserFilter([
                          {
                            type: IFFilterType.KEYWORD,
                            value: [],
                          },
                        ])
                        if (!selectedEmails || selectedEmails.length === 0) {
                          setSelectedEmailsError(
                            t('OperatorAlertList.Required'),
                          )
                        } else {
                          setSelectedEmailsError('')
                        }
                      }}
                    />
                    {selectedEmailsError && (
                      <div className={Styles.ErrorText}>
                        {selectedEmailsError}
                      </div>
                    )}
                  </div>
                ) : (
                  <IFGroupingComponent
                    componentText={clickedOperatorAlert?.emails}
                  />
                )}
              </div>

              <div className={Styles.SettingItem}>
                <IFText
                  className={Styles.SettingTitle}
                  style={{ color: Colors.AlertItem.SettingsTitle }}
                >
                  {t('OperatorAlertList.Active')}
                </IFText>
                <IFToggle
                  key={`${
                    clickedOperatorAlert?._id ?? 'new'
                  }-${isEditing}-${String(isActive)}`}
                  height={16}
                  defaultChecked={!!isActive}
                  marginLeftRight={8}
                  readOnly={!isEditing}
                  onChange={(value) => setIsActive(value)}
                />
              </div>

              <div className={Styles.SettingItem}>
                <div className={Styles.SettingLabel}>
                  <IFText style={{ color: Colors.AlertItem.SettingsTitle }}>
                    {t('OperatorAlertList.Global')}
                  </IFText>
                  <InfoIcon
                    className={Styles.infoIcon}
                    onMouseEnter={(e) => handlePopoverOpen(e, 'global')}
                    onMouseLeave={handlePopoverClose}
                  />
                </div>

                <IFToggle
                  key={`${
                    clickedOperatorAlert?._id ?? 'new'
                  }-${isEditing}-${String(isGlobal)}`}
                  height={16}
                  defaultChecked={!!isGlobal}
                  marginLeftRight={8}
                  readOnly={!isEditing}
                  onChange={(value) => setIsGlobal(value)}
                />
              </div>
            </div>
          </div>
          <div className={Styles.ChargePointPart}>
            <div className={Styles.ChargePointTitle}>
              <IFText className={Styles.SettingsTitle}>
                {isGlobal
                  ? t('OperatorAlertList.ExcludeChargePoints')
                  : t('OperatorAlertList.ChargePoints')}
              </IFText>
            </div>
            <div className={Styles.ChargePointsContainer}>
              <ChargePointsContainer
                key={`cp-container-${isGlobal}`}
                isEditing={isEditing}
                displayedList={displayedList}
                maxListHeight={415}
                ref={chargePointSpecsEditRef}
              />
            </div>
          </div>
        </div>
      </div>
      {canEditAlert && (
        <div className={Styles.ButtonsContainer}>
          {isNew ? (
            <IFButton
              color={Colors.primary}
              isFill
              text={t('OperatorAlertList.Save')}
              onClick={onSaveClick}
              size="xsm"
              className={Styles.Button}
              isLoading={createAlertRequestState === RequestState.LOADING}
              isDead={
                nameError ||
                !name ||
                typeError ||
                !alertType ||
                statusError ||
                !statusValues ||
                durationError ||
                !duration ||
                intervalError ||
                !interval ||
                percentageAffectedError ||
                repeatError ||
                selectedEmailsError ||
                selectedEmails.length === 0
              }
            />
          ) : (
            <>
              <IFButton
                color={isEditing ? Colors.primary : Colors.red}
                isFill={false}
                text={
                  isEditing
                    ? t('OperatorAlertList.Cancel')
                    : t('OperatorAlertList.Delete')
                }
                onClick={isEditing ? onCancelClick : onDeleteClickHandler}
                size="xsm"
                className={Styles.Button}
              />
              <IFButton
                color={Colors.primary}
                isFill
                text={
                  isEditing
                    ? t('OperatorAlertList.Save')
                    : t('OperatorAlertList.Edit')
                }
                onClick={isEditing ? onSaveClick : () => setIsEditing(true)}
                size="xsm"
                className={Styles.Button}
                isLoading={editAlertRequestState === RequestState.LOADING}
                isDead={
                  isEditing &&
                  (name.length === 0 ||
                    alertType === null ||
                    alertType.length === 0 ||
                    statusValues.length === 0 ||
                    duration.length === 0 ||
                    selectedEmails.length === 0 ||
                    nameError ||
                    typeError ||
                    statusError ||
                    durationError ||
                    intervalError ||
                    percentageAffectedError ||
                    repeatError ||
                    selectedEmailsError ||
                    !interval)
                }
              />
            </>
          )}
        </div>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{ sx: { pointerEvents: 'none', p: 0, maxWidth: 360 } }}
      >
        <Box
          className={Styles.ParentBox}
          sx={{
            p: 2,
            maxWidth: 360,
          }}
        >
          <div className={Styles.PopoverContent}>
            <IFText className={Styles.PopoverText}>
              {contentMap[openKey] || ''}
            </IFText>
          </div>
        </Box>
      </Popover>

      <IFDialog
        ref={deleteModalRef}
        dialogWidth="32rem"
        title={t('OperatorAlertList.deleteModalTitle')}
        subtitle={t('OperatorAlertList.deleteModalText')}
        buttonCancelColor={Colors.primary}
        actionButtonColor={Colors.red}
        actionButtonText={t('OperatorAlertList.deleteModalConfirm')}
        buttonClassName={Styles.Button}
        isLoading={deleteAlertRequestState === RequestState.LOADING}
        onAction={handleDelete}
      />
    </div>
  )
}
const mapStateToProps = (state) => ({
  clickedOperatorAlert: OperatorAlertsSelectors.getClickedOperatorAlert(state),
  fetchOperatorUserListRequestState:
    OperatorUserSelectors.getFetchOperatorUserListRequestState(state),
  operatorUsersFilters: OperatorUserSelectors.getOperatorUsersFilters(state),
  operatorsAlerts: OperatorAlertsSelectors.getOperatorsAlerts(state),
  operatorUsersAlertEmails:
    OperatorUserSelectors.getOperatorUsersAlertEmails(state),
  operatorUsersPagination:
    OperatorUserSelectors.getOperatorUsersPagination(state),
  editAlertRequestState:
    OperatorAlertsSelectors.getEditAlertRequestState(state),
  operatorUser: AuthSelectors.getOperatorUser(state),
  deleteAlertRequestState:
    OperatorAlertsSelectors.getDeleteAlertRequestState(state),
  createAlertRequestState:
    OperatorAlertsSelectors.getCreateAlertRequestState(state),
})
function mapDispatchToProps(dispatch) {
  return {
    fetchOperatorUserList: (filter, offset) =>
      dispatch(OperatorUserActions.fetchOperatorUserList(filter, offset)),
    clearOperatorUsers: () =>
      dispatch(OperatorUserActions.clearOperatorUsers()),
    setOperatorUserFilter: (filter) =>
      dispatch(OperatorUserActions.setOperatorUserFilter(filter)),
    editAlert: (alertId, alert) =>
      dispatch(OperatorAlertsActions.editAlert(alertId, alert)),
    deleteAlert: (alertId) =>
      dispatch(OperatorAlertsActions.deleteAlert(alertId)),
    createAlert: (alert) => dispatch(OperatorAlertsActions.createAlert(alert)),
  }
}

OperatorAlertDetails.propTypes = {
  clickedOperatorAlert: PropTypes.object.isRequired,
  operatorUsersFilters: PropTypes.array.isRequired,
  operatorUsersPagination: PropTypes.object.isRequired,
  fetchOperatorUserList: PropTypes.func.isRequired,
  setOperatorUserFilter: PropTypes.func.isRequired,
  clearOperatorUsers: PropTypes.func.isRequired,
  fetchOperatorUserListRequestState: PropTypes.string.isRequired,
  operatorUsersAlertEmails: PropTypes.array.isRequired,
  editAlert: PropTypes.func.isRequired,
  editAlertRequestState: PropTypes.string.isRequired,
  operatorUser: PropTypes.object.isRequired,
  deleteAlert: PropTypes.func.isRequired,
  deleteAlertRequestState: PropTypes.string.isRequired,
}
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OperatorAlertDetails)
